import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useEntityList } from '../data/entities';
import { useToast } from '../components/Toast';
import PoseDictionary from './PoseDictionary';
import {
  AUDIENCES,
  SKILLS,
  STYLES,
  filterLessons,
  lessonStats,
  styleLabel,
  syncSamplePack,
} from './lessonCatalog';
import { useUserProfile } from './useUserProfile';
import { planLabel } from './planTypes';
import { resolvePoseRef } from './poseImages';

function ChipRow({ options, value, onChange, label }) {
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      <span className="u-xs u-muted">{label}</span>
      <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }} role="group" aria-label={label}>
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className="cs-chip"
            aria-pressed={value === opt.id}
            onClick={() => onChange(opt.id)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ClassCard({ lesson, planType }) {
  const navigate = useNavigate();
  const { poseCount, durationMins, sequence } = lessonStats(lesson, planType);
  const first = sequence[0];
  const thumb = first ? resolvePoseRef(first.poseKey) : null;

  return (
    <article className="yoga-class-card">
      {thumb?.url ? (
        <img className="yoga-class-card__img" src={thumb.url} alt="" loading="lazy" />
      ) : (
        <div className="yoga-class-card__img yoga-class-card__img--empty" aria-hidden />
      )}
      <div className="yoga-class-card__body">
        <h3 style={{ margin: 0, fontFamily: 'var(--yoga-display)', fontWeight: 400, fontSize: '1.1rem' }}>
          {lesson.title}
        </h3>
        {lesson.description ? (
          <p className="u-muted u-small" style={{ margin: 0, lineHeight: 1.45 }}>
            {lesson.description}
          </p>
        ) : null}
        <div className="yoga-class-card__meta">
          <span className="cs-badge">{styleLabel(lesson.style)}</span>
          <span className="cs-badge">{lesson.skillLevel}</span>
          <span className="cs-badge">{lesson.audience}</span>
          <span className="cs-badge">{durationMins} min</span>
          <span className="cs-badge">{poseCount} pose{poseCount === 1 ? '' : 's'}</span>
        </div>
        <button
          type="button"
          className="cs-btn primary cs-btn-sm"
          style={{ marginTop: 'auto', alignSelf: 'flex-start' }}
          onClick={() => navigate(`/practice/${lesson.id}`)}
        >
          Start practice
        </button>
      </div>
    </article>
  );
}

function ClassesTab() {
  const toast = useToast();
  const { uid, planType } = useUserProfile();
  const { docs: lessons, error } = useEntityList('yogaLesson', {});
  const [audience, setAudience] = useState('all');
  const [skill, setSkill] = useState('all');
  const [style, setStyle] = useState('all');
  const autoSyncRan = useRef(false);

  const filtered = useMemo(
    () => filterLessons(lessons, { planType, audience, skill, style }),
    [lessons, planType, audience, skill, style],
  );

  useEffect(() => {
    if (!uid || !planType || lessons == null) return;
    if (autoSyncRan.current) return;
    autoSyncRan.current = true;
    let cancelled = false;
    (async () => {
      try {
        const { created, updated } = await syncSamplePack(lessons, { force: false });
        if (cancelled) return;
        if (created || updated) toast('Class library updated', 'ok');
      } catch (err) {
        if (!cancelled) toast(`Could not update library: ${err.message}`, 'danger', 8000);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, planType, lessons, toast]);

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-5)' }}>
      <div className="yoga-filters u-stack" style={{ '--gap': 'var(--sp-4)' }}>
        <ChipRow label="Yoga type" options={STYLES} value={style} onChange={setStyle} />
        <ChipRow label="Audience" options={AUDIENCES} value={audience} onChange={setAudience} />
        <ChipRow label="Skill level" options={SKILLS} value={skill} onChange={setSkill} />
      </div>

      {planType ? (
        <p className="u-muted u-small" style={{ margin: 0 }}>
          Plan: <strong>{planLabel(planType)}</strong>
          {planType === 'karma' ? ' · Beginner Hatha pack' : ' · Full catalog'}
          {' · '}
          <Link to="/membership">Membership</Link>
        </p>
      ) : null}

      {error && (
        <div className="cs-banner b-danger" role="alert">
          Could not load classes: {error}
        </div>
      )}

      {filtered === null && (
        <p className="cs-help">
          <span className="cs-spinner" aria-hidden /> Loading classes…
        </p>
      )}

      {filtered && !filtered.length && (
        <div className="cs-empty">
          No classes match these filters.
          {planType === 'karma'
            ? ' Karma Yogi only sees the beginner Hatha pack — try All styles, or switch plan in Membership.'
            : ' Sample classes refresh automatically when you visit — hang tight, or widen the filters.'}
        </div>
      )}

      {filtered && filtered.length > 0 && (
        <div className="yoga-class-grid">
          {filtered.map((lesson) => (
            <ClassCard key={lesson.id} lesson={lesson} planType={planType} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Library() {
  const [search, setSearch] = useSearchParams();
  const tab = search.get('tab') === 'poses' ? 'poses' : 'classes';

  const setTab = (next) => {
    const params = new URLSearchParams(search);
    if (next === 'poses') params.set('tab', 'poses');
    else params.delete('tab');
    setSearch(params, { replace: true });
  };

  return (
    <div className="cs-page yoga-shell u-stack m-fade-in" style={{ '--gap': 'var(--sp-6)' }}>
      <header className="yoga-page-head m-rise">
        <p className="yoga-eyebrow">YogaKai</p>
        <h1 className="yoga-hero-title">Library</h1>
        <p className="yoga-hero-lead">
          Browse timed classes or the pose dictionary — then start a practice.
        </p>
      </header>

      <div className="yoga-seg" role="tablist" aria-label="Library sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'classes'}
          className="yoga-seg__btn"
          onClick={() => setTab('classes')}
        >
          Classes
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'poses'}
          className="yoga-seg__btn"
          onClick={() => setTab('poses')}
        >
          Poses
        </button>
      </div>

      {tab === 'classes' ? <ClassesTab /> : <PoseDictionary embedded />}
    </div>
  );
}
